import React, { useRef } from "react";
import { Bold, Italic, Heading2, Heading3, Quote, List, Sparkles } from "lucide-react";

/**
 * Simple markdown-flavored editor with a formatting toolbar.
 * Buttons wrap the current selection with markdown syntax,
 * or insert the syntax at the cursor position.
 */
export default function RichTextArea({ value, onChange, testid, placeholder, minHeight = 260 }) {
  const ref = useRef(null);

  const applyWrap = (before, after = before) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end) || "texte";
    const newValue =
      value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(newValue);
    // Restore selection right after the inserted content
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + before.length + selected.length;
      el.setSelectionRange(start + before.length, pos);
    });
  };

  const applyLinePrefix = (prefix) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    // Find start of the current line
    const before = value.slice(0, start);
    const lineStart = before.lastIndexOf("\n") + 1;
    // Ensure there's a blank line before headings/quotes/lists (except for lists which chain)
    let insertAt = lineStart;
    let addBlankBefore = "";
    if (lineStart > 0 && value[lineStart - 1] !== "\n") {
      addBlankBefore = "\n\n";
      insertAt = lineStart;
    } else if (lineStart >= 2 && value[lineStart - 2] !== "\n" && value[lineStart - 1] === "\n") {
      addBlankBefore = "\n";
    }
    const newValue =
      value.slice(0, insertAt) + addBlankBefore + prefix + value.slice(insertAt);
    onChange(newValue);
    requestAnimationFrame(() => {
      el.focus();
      const pos = insertAt + addBlankBefore.length + prefix.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const tools = [
    { label: "Grand titre", icon: Heading2, onClick: () => applyLinePrefix("## "), key: "h2" },
    { label: "Petit titre", icon: Heading3, onClick: () => applyLinePrefix("### "), key: "h3" },
    { label: "Gras", icon: Bold, onClick: () => applyWrap("**"), key: "bold" },
    { label: "Italique", icon: Italic, onClick: () => applyWrap("*"), key: "italic" },
    { label: "Citation", icon: Quote, onClick: () => applyLinePrefix("> "), key: "quote" },
    { label: "Liste", icon: List, onClick: () => applyLinePrefix("- "), key: "list" },
  ];

  return (
    <div className="flex flex-col gap-2">
      <div
        data-testid="editor-toolbar"
        className="flex flex-wrap gap-1 p-2 border-2 border-black rounded-xl"
        style={{ background: "#FFFDF7" }}
      >
        {tools.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`tool-${t.key}`}
            onClick={t.onClick}
            title={t.label}
            className="flex items-center gap-1 px-3 py-1.5 border-2 border-black rounded-full hover:bg-[#FFD01E] active:translate-y-[1px] transition-transform text-xs font-bold"
          >
            <t.icon size={14} strokeWidth={2.5} />
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1 text-xs text-black/50 px-2">
          <Sparkles size={12} />
          Sélectionne du texte puis clique
        </div>
      </div>
      <textarea
        ref={ref}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ minHeight }}
        className="border-2 border-black rounded-xl px-3 py-2 bg-white font-mono text-sm"
        required
      />
    </div>
  );
}
